import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageModuleService } from '../manage-module.service';
import { LibMultiSelectComponent } from '../../lib-multi-select/lib-multi-select.component';
import { ModuleItem } from '../manage-modules.component';
import * as _ from 'lodash';

// Interface for module data
interface ModuleData {
  moduleId?: number;
  moduleType: string;
  moduleName: string;
  moduleOwnerEmail: string;
  moduleBrief: string;
  moduleDescription: string;
  moduleFeatures: string[];
  moduleLink: string;
  moduleImagePath: string;
  moduleApiLink: string;
  moduleIdentityToken: string;
  moduleCallbackEndpoint: string;
  report: string;
  group: string;
  additionalGroup: string;
  businessLine: string;
  category: string;
  function: string;
  productLine: string;
  region: string;
  roleName: string;
  tagId: string;
  isInternal: boolean;
  isMultimodal: boolean;
  instantProcessing: boolean;
  actionConfigurations: ActionConfig[];
  accessGroups: AccessGroup[];
  status: string;
  createdBy?: string;
  createdOn?: string;
  updatedBy?: string;
  updatedOn?: string;
}

interface ActionConfig {
  actionName: string;
  isEnabled: boolean;
  forStatuses: string[];
  lockingPeriod: number;
  actionValidity: number;
}

interface AccessGroup {
  groupName: string;
  groupDescription: string;
}

@Component({
  selector: 'app-edit-module',
  // standalone: true,
  // imports: [CommonModule, ReactiveFormsModule, FormsModule, LibMultiSelectComponent],
  templateUrl: './edit-module.component.html',
  styleUrl: './edit-module.component.scss'
})
export class EditModuleComponent implements OnInit, OnDestroy {
  moduleForm!: FormGroup;
  moduleId: number | null = null;
  isEditMode: boolean = false;
  userId: string = '';
  
  // Character limits
  briefCharLimit: number = 1000;
  descriptionCharLimit: number = 1000;
  
  // Form state
  isCancelPopupVisible: boolean = false;
  isFormModified: boolean = false;
  
  // Dropdown options - updated per specs
  moduleTypeOptions = [
    { id: 1, name: 'Dashboard' },
    { id: 2, name: 'Application' },
    { id: 3, name: 'Data Product' }
  ];
  
  businessLineOptions = [
    { id: 1, name: 'AGB' },
    { id: 2, name: 'URB' },
    { id: 3, name: 'TLD' },
    { id: 4, name: 'PCLT' }
  ];
  
  regionOptions = [
    { id: 1, name: 'AMN' },
    { id: 2, name: 'EUS' },
    { id: 3, name: 'EUC' },
    { id: 4, name: 'EUN' },
    { id: 5, name: 'AMC' },
    { id: 6, name: 'AIM' },
    { id: 7, name: 'E2A' }
  ];

  // Signal-based selected items for dropdowns
  selectedModuleType = signal<string[]>([]);
  selectedBusinessLine = signal<string[]>([]);
  selectedRegion = signal<string[]>([]);

  // Dropdown open state management
  openDropdownKey: string | null = null;
  
  // Action configuration options
  actionNames = ['COPY', 'DOWNLOAD', 'EDIT', 'RE-RUN', 'DELETE'];
  statusOptions = ['Completed', 'Draft', 'Failed', 'In Progress', 'Not Started', 'Re-run'];
  lockingPeriodOptions = [0, 1, 7, 14, 30, 60, 90];
  actionValidityOptions = [0, 1, 3, 6, 9, 12];
  
  // Features row count
  featureRowsToAdd: number = 4;
  
  // Access groups row count
  accessGroupRowsToAdd: number = 4;
  
  // Module details for edit mode
  moduleDetails: ModuleItem | null = null;

  // Mock module list - same data as manage-modules component
  moduleList: ModuleItem[] = [
    {
      id: 1,
      moduleType: 'Data Product',
      moduleName: 'Tire Pairing',
      moduleOwnerEmail: 'tyson.clay@example.com',
      moduleBrief: 'Introducing our cutting-edge Tire Pairing module, a meticulously crafted solution designed to elevate your understanding of the tire industry.',
      moduleDescription: 'The Tire Pairing module provides comprehensive analytics and pairing recommendations for optimal tire performance and safety.',
      moduleFeatures: ['Data Analytics', 'Pairing Recommendations', 'Performance Tracking', 'Safety Metrics'],
      moduleLink: '/tire-pairing',
      moduleImagePath: 'assets/images/tire-pairing.png',
      moduleApiLink: 'api/tire-pairing',
      moduleIdentityToken: 'TP-001',
      moduleCallbackEndpoint: '/api/callback/tire-pairing',
      report: 'Tire Report',
      group: 'AGB-TC',
      additionalGroup: 'ARB-TC',
      businessLine: 'AGB',
      category: 'Analytics',
      function: 'Data Processing',
      productLine: 'Tires',
      region: 'AMN',
      roleName: 'Analyst',
      tagId: 'TAG-001',
      isInternal: false,
      isMultimodal: false,
      instantProcessing: true,
      status: 'Active',
      createdBy: 'Tyson Clay',
      createdOn: '2024-05-01',
      updatedBy: 'Tyson Clay',
      updatedOn: '2024-05-01',
      accessGroups: [{ groupName: 'AGB-TC', groupDescription: 'AGB Technical Center' }, { groupName: 'ARB-TC', groupDescription: 'ARB Technical Center' }]
    },
    {
      id: 2,
      moduleType: 'Dashboard',
      moduleName: 'Sales Analytics',
      moduleOwnerEmail: 'sarah.johnson@example.com',
      moduleBrief: 'Comprehensive sales analytics dashboard providing real-time insights into sales performance.',
      moduleDescription: 'Sales Analytics Dashboard offers detailed visualization of sales trends, forecasts, and KPIs across all business lines.',
      moduleFeatures: ['Real-time Data', 'Sales Forecasting', 'KPI Tracking', 'Custom Reports'],
      moduleLink: '/sales-analytics',
      moduleImagePath: 'assets/images/sales-analytics.png',
      moduleApiLink: 'api/sales-analytics',
      moduleIdentityToken: 'SA-002',
      moduleCallbackEndpoint: '/api/callback/sales-analytics',
      report: 'Sales Report',
      group: 'B2B-AG',
      additionalGroup: '2WH-TC',
      businessLine: 'URB',
      category: 'Business Intelligence',
      function: 'Sales Operations',
      productLine: 'Analytics',
      region: 'EUS',
      roleName: 'Sales Manager',
      tagId: 'TAG-002',
      isInternal: true,
      isMultimodal: false,
      instantProcessing: false,
      status: 'Active',
      createdBy: 'Sarah Johnson',
      createdOn: '2024-05-02',
      updatedBy: 'Sarah Johnson',
      updatedOn: '2024-05-02',
      accessGroups: [{ groupName: 'B2B-AG', groupDescription: 'B2B Access Group' }]
    },
    {
      id: 3,
      moduleType: 'Application',
      moduleName: 'Inventory Management',
      moduleOwnerEmail: 'mike.rodriguez@example.com',
      moduleBrief: 'Full-featured inventory management application for tracking stock levels and supply chain.',
      moduleDescription: 'Inventory Management Application provides end-to-end visibility into inventory levels, automated reordering, and supply chain optimization.',
      moduleFeatures: ['Stock Tracking', 'Automated Reordering', 'Supply Chain View', 'Barcode Scanning'],
      moduleLink: '/inventory-management',
      moduleImagePath: 'assets/images/inventory.png',
      moduleApiLink: 'api/inventory',
      moduleIdentityToken: 'IM-003',
      moduleCallbackEndpoint: '/api/callback/inventory',
      report: 'Inventory Report',
      group: 'AGB-TC',
      additionalGroup: '',
      businessLine: 'TLD',
      category: 'Operations',
      function: 'Warehouse Management',
      productLine: 'Logistics',
      region: 'EUC',
      roleName: 'Warehouse Manager',
      tagId: 'TAG-003',
      isInternal: false,
      isMultimodal: true,
      instantProcessing: true,
      status: 'Inactive',
      createdBy: 'Mike Rodriguez',
      createdOn: '2024-05-03',
      updatedBy: 'Mike Rodriguez',
      updatedOn: '2024-05-03',
      accessGroups: [{ groupName: 'AGB-TC', groupDescription: 'AGB Technical Center' }]
    },
    {
      id: 4,
      moduleType: 'Data Product',
      moduleName: 'Performance Metrics',
      moduleOwnerEmail: 'emma.wilson@example.com',
      moduleBrief: 'Data product for analyzing and visualizing key performance metrics across the organization.',
      moduleDescription: 'Performance Metrics provides comprehensive analysis of organizational KPIs with drill-down capabilities and trend analysis.',
      moduleFeatures: ['KPI Analysis', 'Trend Visualization', 'Drill-down Reports', 'Custom Dashboards'],
      moduleLink: '/performance-metrics',
      moduleImagePath: 'assets/images/performance.png',
      moduleApiLink: 'api/performance',
      moduleIdentityToken: 'PM-004',
      moduleCallbackEndpoint: '/api/callback/performance',
      report: 'Performance Report',
      group: 'ARB-TC',
      additionalGroup: 'B2B-AG',
      businessLine: 'PCLT',
      category: 'Analytics',
      function: 'Performance Analysis',
      productLine: 'Business',
      region: 'EUN',
      roleName: 'Business Analyst',
      tagId: 'TAG-004',
      isInternal: true,
      isMultimodal: false,
      instantProcessing: true,
      status: 'Active',
      createdBy: 'Emma Wilson',
      createdOn: '2024-05-04',
      updatedBy: 'Emma Wilson',
      updatedOn: '2024-05-04',
      accessGroups: [{ groupName: 'ARB-TC', groupDescription: 'ARB Technical Center' }, { groupName: 'B2B-AG', groupDescription: 'B2B Access Group' }]
    },
    {
      id: 5,
      moduleType: 'Dashboard',
      moduleName: 'Quality Control',
      moduleOwnerEmail: 'david.chen@example.com',
      moduleBrief: 'Quality control dashboard for monitoring and managing product quality metrics.',
      moduleDescription: 'Quality Control Dashboard provides real-time monitoring of quality metrics, defect tracking, and compliance reporting.',
      moduleFeatures: ['Quality Monitoring', 'Defect Tracking', 'Compliance Reports', 'Alert System'],
      moduleLink: '/quality-control',
      moduleImagePath: 'assets/images/quality.png',
      moduleApiLink: 'api/quality',
      moduleIdentityToken: 'QC-005',
      moduleCallbackEndpoint: '/api/callback/quality',
      report: 'Quality Report',
      group: 'AGB-TC',
      additionalGroup: 'B2B-AG',
      businessLine: 'AGB',
      category: 'Quality',
      function: 'Quality Assurance',
      productLine: 'Manufacturing',
      region: 'AMC',
      roleName: 'Quality Manager',
      tagId: 'TAG-005',
      isInternal: false,
      isMultimodal: false,
      instantProcessing: false,
      status: 'Inactive',
      createdBy: 'David Chen',
      createdOn: '2024-05-05',
      updatedBy: 'David Chen',
      updatedOn: '2024-05-05',
      accessGroups: [{ groupName: 'AGB-TC', groupDescription: 'AGB Technical Center' }]
    },
    {
      id: 6,
      moduleType: 'Application',
      moduleName: 'Customer Portal',
      moduleOwnerEmail: 'lisa.anderson@example.com',
      moduleBrief: 'Self-service customer portal for managing orders, tracking shipments, and accessing support.',
      moduleDescription: 'Customer Portal Application enables customers to manage their accounts, place orders, track shipments, and access 24/7 support.',
      moduleFeatures: ['Order Management', 'Shipment Tracking', 'Support Portal', 'Account Management'],
      moduleLink: '/customer-portal',
      moduleImagePath: 'assets/images/customer-portal.png',
      moduleApiLink: 'api/customer-portal',
      moduleIdentityToken: 'CP-006',
      moduleCallbackEndpoint: '/api/callback/customer',
      report: 'Customer Report',
      group: '2WH-TC',
      additionalGroup: 'AGB-TC',
      businessLine: 'URB',
      category: 'Customer Service',
      function: 'Customer Relations',
      productLine: 'Services',
      region: 'AIM',
      roleName: 'Customer Success Manager',
      tagId: 'TAG-006',
      isInternal: false,
      isMultimodal: true,
      instantProcessing: true,
      status: 'Inactive',
      createdBy: 'Lisa Anderson',
      createdOn: '2024-05-06',
      updatedBy: 'Lisa Anderson',
      updatedOn: '2024-05-06',
      accessGroups: [{ groupName: '2WH-TC', groupDescription: '2WH Technical Center' }]
    },
    {
      id: 7,
      moduleType: 'Data Product',
      moduleName: 'Fleet Management',
      moduleOwnerEmail: 'james.martinez@example.com',
      moduleBrief: 'Comprehensive fleet management data product for tracking vehicle performance and maintenance.',
      moduleDescription: 'Fleet Management provides real-time tracking of vehicle fleets, maintenance schedules, fuel consumption analysis, and driver performance metrics.',
      moduleFeatures: ['Vehicle Tracking', 'Maintenance Scheduling', 'Fuel Analysis', 'Driver Metrics'],
      moduleLink: '/fleet-management',
      moduleImagePath: 'assets/images/fleet.png',
      moduleApiLink: 'api/fleet',
      moduleIdentityToken: 'FM-007',
      moduleCallbackEndpoint: '/api/callback/fleet',
      report: 'Fleet Report',
      group: 'B2B-AG',
      additionalGroup: '',
      businessLine: 'TLD',
      category: 'Operations',
      function: 'Fleet Operations',
      productLine: 'Transport',
      region: 'E2A',
      roleName: 'Fleet Manager',
      tagId: 'TAG-007',
      isInternal: true,
      isMultimodal: false,
      instantProcessing: false,
      status: 'Inactive',
      createdBy: 'James Martinez',
      createdOn: '2024-05-07',
      updatedBy: 'James Martinez',
      updatedOn: '2024-05-07',
      accessGroups: [{ groupName: 'B2B-AG', groupDescription: 'B2B Access Group' }]
    },
    {
      id: 8,
      moduleType: 'Dashboard',
      moduleName: 'Production Overview',
      moduleOwnerEmail: 'rachel.green@example.com',
      moduleBrief: 'Production overview dashboard for monitoring manufacturing operations in real-time.',
      moduleDescription: 'Production Overview Dashboard displays real-time production metrics, OEE calculations, and production line status across all manufacturing facilities.',
      moduleFeatures: ['Production Monitoring', 'OEE Tracking', 'Line Status', 'Shift Reports'],
      moduleLink: '/production-overview',
      moduleImagePath: 'assets/images/production.png',
      moduleApiLink: 'api/production',
      moduleIdentityToken: 'PO-008',
      moduleCallbackEndpoint: '/api/callback/production',
      report: 'Production Report',
      group: 'ARB-TC',
      additionalGroup: '2WH-TC',
      businessLine: 'PCLT',
      category: 'Manufacturing',
      function: 'Production Management',
      productLine: 'Manufacturing',
      region: 'AMN',
      roleName: 'Production Manager',
      tagId: 'TAG-008',
      isInternal: false,
      isMultimodal: false,
      instantProcessing: true,
      status: 'Inactive',
      createdBy: 'Rachel Green',
      createdOn: '2024-05-08',
      updatedBy: 'Rachel Green',
      updatedOn: '2024-05-08',
      accessGroups: [{ groupName: 'ARB-TC', groupDescription: 'ARB Technical Center' }, { groupName: '2WH-TC', groupDescription: '2WH Technical Center' }]
    },
    {
      id: 9,
      moduleType: 'Application',
      moduleName: 'Supply Chain',
      moduleOwnerEmail: 'kevin.brown@example.com',
      moduleBrief: 'End-to-end supply chain management application for optimizing logistics and procurement.',
      moduleDescription: 'Supply Chain Application provides comprehensive tools for managing suppliers, procurement processes, logistics, and supply chain optimization.',
      moduleFeatures: ['Supplier Management', 'Procurement', 'Logistics Optimization', 'Cost Analysis'],
      moduleLink: '/supply-chain',
      moduleImagePath: 'assets/images/supply-chain.png',
      moduleApiLink: 'api/supply-chain',
      moduleIdentityToken: 'SC-009',
      moduleCallbackEndpoint: '/api/callback/supply-chain',
      report: 'Supply Chain Report',
      group: 'AGB-TC',
      additionalGroup: 'B2B-AG',
      businessLine: 'AGB',
      category: 'Logistics',
      function: 'Supply Chain Management',
      productLine: 'Operations',
      region: 'EUS',
      roleName: 'Supply Chain Manager',
      tagId: 'TAG-009',
      isInternal: true,
      isMultimodal: true,
      instantProcessing: true,
      status: 'Active',
      createdBy: 'Kevin Brown',
      createdOn: '2024-05-09',
      updatedBy: 'Kevin Brown',
      updatedOn: '2024-05-09',
      accessGroups: [{ groupName: 'AGB-TC', groupDescription: 'AGB Technical Center' }, { groupName: 'B2B-AG', groupDescription: 'B2B Access Group' }]
    },
    {
      id: 10,
      moduleType: 'Data Product',
      moduleName: 'Market Analysis',
      moduleOwnerEmail: 'amanda.taylor@example.com',
      moduleBrief: 'Market analysis data product for understanding market trends and competitive landscape.',
      moduleDescription: 'Market Analysis provides in-depth market research data, competitor analysis, and trend forecasting for strategic decision-making.',
      moduleFeatures: ['Market Research', 'Competitor Analysis', 'Trend Forecasting', 'Strategic Insights'],
      moduleLink: '/market-analysis',
      moduleImagePath: 'assets/images/market.png',
      moduleApiLink: 'api/market-analysis',
      moduleIdentityToken: 'MA-010',
      moduleCallbackEndpoint: '/api/callback/market',
      report: 'Market Report',
      group: '2WH-TC',
      additionalGroup: '',
      businessLine: 'URB',
      category: 'Market Intelligence',
      function: 'Market Research',
      productLine: 'Strategy',
      region: 'EUC',
      roleName: 'Market Analyst',
      tagId: 'TAG-010',
      isInternal: false,
      isMultimodal: false,
      instantProcessing: false,
      status: 'Active',
      createdBy: 'Amanda Taylor',
      createdOn: '2024-05-10',
      updatedBy: 'Amanda Taylor',
      updatedOn: '2024-05-10',
      accessGroups: [{ groupName: '2WH-TC', groupDescription: '2WH Technical Center' }]
    }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private moduleService: ManageModuleService
  ) {
    // Retrieve navigation state for create mode metadata
    const navigation = this.router.getCurrentNavigation();
    this.createModeMetadata = navigation?.extras?.state?.['moduleMetadata'] || null;
  }

  // Store metadata passed from create module modal
  private createModeMetadata: any = null;

  ngOnInit(): void {
    this.userId = localStorage.getItem('userId') || '';
    this.moduleId = +this.route.snapshot.params['moduleId'] || null;
    this.isEditMode = !!this.moduleId;
    
    this.initForm();
    this.initActionConfigurations();
    this.initDefaultFeatures();
    this.initDefaultAccessGroups();
    
    if (this.isEditMode && this.moduleId) {
      this.loadModuleDetails();
    } else if (!this.isEditMode && this.createModeMetadata) {
      // Create mode with metadata from modal
      this.initCreateModeWithMetadata();
    }
  }

  // Initialize create mode with metadata from modal
  private initCreateModeWithMetadata(): void {
    const metadata = this.createModeMetadata;
    
    // Create a partial moduleDetails object for displaying metadataRow
    this.moduleDetails = {
      id: 0,
      moduleType: metadata.moduleType,
      moduleName: '',
      moduleOwnerEmail: '',
      moduleBrief: '',
      moduleDescription: '',
      moduleFeatures: [],
      moduleLink: '',
      moduleImagePath: '',
      moduleApiLink: '',
      moduleIdentityToken: '',
      moduleCallbackEndpoint: '',
      report: '',
      group: '',
      additionalGroup: '',
      businessLine: '',
      category: '',
      function: '',
      productLine: '',
      region: '',
      roleName: '',
      tagId: '',
      isInternal: false,
      isMultimodal: false,
      instantProcessing: true,
      status: metadata.status || 'Inactive',
      createdBy: metadata.createdBy,
      createdOn: metadata.createdOn,
      updatedBy: metadata.updatedBy,
      updatedOn: metadata.updatedOn,
      accessGroups: []
    };

    // Set the module type in the form and signal
    this.selectedModuleType.set([metadata.moduleType]);
    this.moduleForm.patchValue({
      moduleType: metadata.moduleType,
      status: metadata.status === 'Active'
    });
  }

  ngOnDestroy(): void {}

  initForm(): void {
    this.moduleForm = this.fb.group({
      // Module Details For User
      moduleType: ['', Validators.required],
      moduleName: ['', Validators.required],
      moduleOwnerEmail: ['', [Validators.required, Validators.email]],
      moduleBrief: ['', [Validators.required, Validators.maxLength(this.briefCharLimit)]],
      moduleDescription: ['', [Validators.required, Validators.maxLength(this.descriptionCharLimit)]],
      moduleFeatures: this.fb.array([]),
      
      // Module Configuration
      moduleLink: ['', Validators.required],
      moduleImagePath: ['', Validators.required],
      moduleApiLink: [''],
      moduleIdentityToken: [''],
      moduleCallbackEndpoint: [''],
      report: [''],
      group: [''],
      additionalGroup: [''],
      businessLine: [''],
      category: [''],
      function: [''],
      productLine: [''],
      region: [''],
      roleName: [''],
      tagId: [''],
      isInternal: [false],
      isMultimodal: [false],
      instantProcessing: [true],
      
      // Action Configuration
      actionConfigurations: this.fb.array([]),
      
      // Access Group Configuration
      accessGroups: this.fb.array([]),
      
      // Status
      status: [true]
    });

    // Track form changes
    this.moduleForm.valueChanges.subscribe(() => {
      this.isFormModified = true;
    });
  }

  // Feature methods
  get features(): FormArray {
    return this.moduleForm.get('moduleFeatures') as FormArray;
  }

  initDefaultFeatures(): void {
    for (let i = 0; i < 4; i++) {
      this.addFeature();
    }
  }

  addFeature(value: string = ''): void {
    this.features.push(new FormControl(value));
  }

  addMultipleFeatures(): void {
    for (let i = 0; i < this.featureRowsToAdd; i++) {
      this.addFeature();
    }
  }

  removeFeature(index: number): void {
    this.features.removeAt(index);
  }

  clearAllFeatures(): void {
    while (this.features.length > 0) {
      this.features.removeAt(0);
    }
    this.initDefaultFeatures();
  }

  // Action Configuration methods
  get actionConfigs(): FormArray {
    return this.moduleForm.get('actionConfigurations') as FormArray;
  }

  initActionConfigurations(): void {
    this.actionNames.forEach(actionName => {
      this.actionConfigs.push(this.fb.group({
        actionName: [actionName],
        isEnabled: [true],
        forStatuses: [this.statusOptions.slice()],
        lockingPeriod: [0],
        actionValidity: [0]
      }));
    });
  }

  toggleActionStatus(index: number, status: string): void {
    const control = this.actionConfigs.at(index);
    const currentStatuses = control.get('forStatuses')?.value || [];
    const statusIndex = currentStatuses.indexOf(status);
    
    if (statusIndex > -1) {
      currentStatuses.splice(statusIndex, 1);
    } else {
      currentStatuses.push(status);
    }
    
    control.get('forStatuses')?.setValue([...currentStatuses]);
  }

  isStatusSelected(actionIndex: number, status: string): boolean {
    const statuses = this.actionConfigs.at(actionIndex).get('forStatuses')?.value || [];
    return statuses.includes(status);
  }

  deselectAllStatuses(actionIndex: number): void {
    this.actionConfigs.at(actionIndex).get('forStatuses')?.setValue([]);
  }

  // Access Group methods
  get accessGroups(): FormArray {
    return this.moduleForm.get('accessGroups') as FormArray;
  }

  initDefaultAccessGroups(): void {
    for (let i = 0; i < 4; i++) {
      this.addAccessGroup();
    }
  }

  addAccessGroup(groupName: string = '', groupDescription: string = ''): void {
    this.accessGroups.push(this.fb.group({
      groupName: [groupName, Validators.required],
      groupDescription: [groupDescription, Validators.required]
    }));
  }

  addMultipleAccessGroups(): void {
    for (let i = 0; i < this.accessGroupRowsToAdd; i++) {
      this.addAccessGroup();
    }
  }

  removeAccessGroup(index: number): void {
    this.accessGroups.removeAt(index);
  }

  clearAllAccessGroups(): void {
    while (this.accessGroups.length > 0) {
      this.accessGroups.removeAt(0);
    }
    this.initDefaultAccessGroups();
  }

  // Character count methods
  getBriefCharCount(): number {
    return this.moduleForm.get('moduleBrief')?.value?.length || 0;
  }

  getDescriptionCharCount(): number {
    return this.moduleForm.get('moduleDescription')?.value?.length || 0;
  }

  // Load module details for edit mode
  loadModuleDetails(): void {
    // Find module from mock data by ID
    const module = this.moduleList.find(m => m.id === this.moduleId);
    
    if (module) {
      this.moduleDetails = module;
      // Populate form with module details
      this.populateForm();
    } else {
      console.error('Module not found with ID:', this.moduleId);
      this.goBack();
    }
  }

  populateForm(): void {
    if (!this.moduleDetails) return;

    // Set signal values for dropdowns
    this.selectedModuleType.set([this.moduleDetails.moduleType]);
    this.selectedBusinessLine.set([this.moduleDetails.businessLine]);
    this.selectedRegion.set([this.moduleDetails.region]);

    this.moduleForm.patchValue({
      moduleType: this.moduleDetails.moduleType,
      moduleName: this.moduleDetails.moduleName,
      moduleOwnerEmail: this.moduleDetails.moduleOwnerEmail,
      moduleBrief: this.moduleDetails.moduleBrief,
      moduleDescription: this.moduleDetails.moduleDescription,
      moduleLink: this.moduleDetails.moduleLink,
      moduleImagePath: this.moduleDetails.moduleImagePath,
      moduleApiLink: this.moduleDetails.moduleApiLink,
      moduleIdentityToken: this.moduleDetails.moduleIdentityToken,
      moduleCallbackEndpoint: this.moduleDetails.moduleCallbackEndpoint,
      report: this.moduleDetails.report,
      group: this.moduleDetails.group,
      additionalGroup: this.moduleDetails.additionalGroup,
      businessLine: this.moduleDetails.businessLine,
      category: this.moduleDetails.category,
      function: this.moduleDetails.function,
      productLine: this.moduleDetails.productLine,
      region: this.moduleDetails.region,
      roleName: this.moduleDetails.roleName,
      tagId: this.moduleDetails.tagId,
      isInternal: this.moduleDetails.isInternal,
      isMultimodal: this.moduleDetails.isMultimodal,
      instantProcessing: this.moduleDetails.instantProcessing,
      status: this.moduleDetails.status === 'Active'
    });

    // Populate features
    if (this.moduleDetails.moduleFeatures) {
      this.features.clear();
      this.moduleDetails.moduleFeatures.forEach((feature: string) => {
        this.addFeature(feature);
      });
    }

    // Populate access groups if available
    if (this.moduleDetails.accessGroups) {
      this.accessGroups.clear();
      this.moduleDetails.accessGroups.forEach((group: AccessGroup) => {
        this.addAccessGroup(group.groupName, group.groupDescription);
      });
    }

    this.isFormModified = false;
  }

  // Navigation methods
  goBack(): void {
    this.router.navigate(['/manage-modules']);
  }

  onCancel(): void {
    if (this.isFormModified) {
      this.isCancelPopupVisible = true;
    } else {
      this.goBack();
    }
  }

  confirmCancel(): void {
    this.isCancelPopupVisible = false;
    this.goBack();
  }

  closePopup(): void {
    this.isCancelPopupVisible = false;
  }

  // Form submission
  onSubmit(): void {
    if (this.moduleForm.valid) {
      const formData = this.prepareFormData();
      console.log('Submitting module:', formData);
      // this.moduleService.createModule(formData).subscribe(...)
      this.goBack();
    } else {
      this.markFormGroupTouched();
    }
  }

  onSaveChanges(): void {
    if (this.moduleForm.valid) {
      const formData = this.prepareFormData();
      console.log('Saving module changes:', formData);
      // this.moduleService.updateModule(formData).subscribe(...)
      this.goBack();
    } else {
      this.markFormGroupTouched();
    }
  }

  onReset(): void {
    if (this.isEditMode) {
      this.populateForm();
    } else {
      this.moduleForm.reset();
      this.features.clear();
      this.accessGroups.clear();
      this.initDefaultFeatures();
      this.initDefaultAccessGroups();
    }
    this.isFormModified = false;
  }

  private prepareFormData(): ModuleData {
    const formValue = this.moduleForm.value;
    return {
      moduleId: this.moduleId || undefined,
      moduleType: formValue.moduleType,
      moduleName: formValue.moduleName,
      moduleOwnerEmail: formValue.moduleOwnerEmail,
      moduleBrief: formValue.moduleBrief,
      moduleDescription: formValue.moduleDescription,
      moduleFeatures: formValue.moduleFeatures.filter((f: string) => f && f.trim() !== ''),
      moduleLink: formValue.moduleLink,
      moduleImagePath: formValue.moduleImagePath,
      moduleApiLink: formValue.moduleApiLink,
      moduleIdentityToken: formValue.moduleIdentityToken,
      moduleCallbackEndpoint: formValue.moduleCallbackEndpoint,
      report: formValue.report,
      group: formValue.group,
      additionalGroup: formValue.additionalGroup,
      businessLine: formValue.businessLine,
      category: formValue.category,
      function: formValue.function,
      productLine: formValue.productLine,
      region: formValue.region,
      roleName: formValue.roleName,
      tagId: formValue.tagId,
      isInternal: formValue.isInternal,
      isMultimodal: formValue.isMultimodal,
      instantProcessing: formValue.instantProcessing,
      actionConfigurations: formValue.actionConfigurations,
      accessGroups: formValue.accessGroups.filter((g: AccessGroup) => g.groupName && g.groupName.trim() !== ''),
      status: formValue.status ? 'Active' : 'Inactive'
    };
  }

  private markFormGroupTouched(): void {
    Object.keys(this.moduleForm.controls).forEach(key => {
      const control = this.moduleForm.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched();
      }
    });
  }

  // File upload handler for access groups
  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      // Handle file upload - parse CSV/Excel and populate access groups
      console.log('File selected:', file.name);
    }
  }

  // Check if form is valid for submission
  isFormValid(): boolean {
    return this.moduleForm.valid && 
           this.features.length > 0 && 
           this.features.controls.some(c => c.value && c.value.trim() !== '');
  }

  // Dropdown toggle handler
  onMultiSelectDropdownToggle(key: string): void {
    this.openDropdownKey = this.openDropdownKey === key ? null : key;
  }

  // Module Type selection handler
  onModuleTypeSelectionChange(selectedTypes: string[]): void {
    this.selectedModuleType.set(selectedTypes);
    if (selectedTypes.length > 0) {
      this.moduleForm.patchValue({ moduleType: selectedTypes[0] });
    }
  }

  // Business Line selection handler
  onBusinessLineSelectionChange(selectedLines: string[]): void {
    this.selectedBusinessLine.set(selectedLines);
    if (selectedLines.length > 0) {
      this.moduleForm.patchValue({ businessLine: selectedLines[0] });
    }
  }

  // Region selection handler
  onRegionSelectionChange(selectedRegions: string[]): void {
    this.selectedRegion.set(selectedRegions);
    if (selectedRegions.length > 0) {
      this.moduleForm.patchValue({ region: selectedRegions[0] });
    }
  }
}

